/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Transfer To Product Dialog V2
 *
 * Dialog zum Überführen von Bildern zu Produkten.
 * Mit Inline-Tabellenansicht für Produktauswahl.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BoxSelect, Search as SearchIcon, X, AlertCircle, Check } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { Button, Input, Select, Badge } from '@/components/v2/ui';
import { getProducts } from '@/lib/api/products';
import { transferFromGalleryToProduct } from '@/lib/api/product-images';
import { cn } from '@/lib/utils';
import type { Product } from '@/types/products';
import type { Image } from '@/types';

export interface TransferToProductDialogProps {
  show: boolean;
  image: Image | null;
  onClose: () => void;
  onSuccess?: (assetId: string) => void;
}

export function TransferToProductDialog({
  show,
  image,
  onClose,
  onSuccess,
}: TransferToProductDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [targetType, setTargetType] = useState<'ai_reference' | 'collection'>('collection');
  const [selectedSlot, setSelectedSlot] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

   
  useEffect(() => {
    if (show) {
      setSelectedProductId('');
      setTargetType('collection');
      setSelectedSlot(1);
      setSearchQuery('');
      setError(null);
    }
  }, [show, image]);

  // Produkte laden
  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products', searchQuery],
    queryFn: () =>
      getProducts({
        search: searchQuery || undefined,
        page_size: 50,
      }),
    enabled: show,
  });

  // Transfer Mutation
  const transferMutation = useMutation({
    mutationFn: async () => {
      if (!image?.id || !selectedProductId) {
        throw new Error('Bild oder Produkt nicht ausgewählt');
      }
      return transferFromGalleryToProduct(image.id, selectedProductId, {
        targetType,
        // Slot nur für KI-Referenz senden
        slot: targetType === 'ai_reference' ? selectedSlot : undefined,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['images'] });
        queryClient.invalidateQueries({ queryKey: ['product-ai-images', selectedProductId] });
        queryClient.invalidateQueries({ queryKey: ['product-ai-references', selectedProductId] });
        const successMsg = targetType === 'ai_reference'
          ? t('toast.success.imageAddedToProductSlot', { slot: selectedSlot })
          : t('toast.success.imageAddedToProduct');
        toast.success(successMsg);
        onSuccess?.(result.asset_id || '');
        onClose();
      } else {
        setError(result.error || 'Transfer fehlgeschlagen');
      }
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const products = productsData?.products || [];

  const handleTransfer = () => {
    setError(null);
    transferMutation.mutate();
  };

  return (
    <Dialog.Root open={show} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
            'w-full max-w-2xl max-h-[90vh] flex flex-col',
            'bg-white dark:bg-neutral-900 rounded-xl shadow-xl',
            'focus:outline-none'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
            <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-neutral-900 dark:text-white">
              <BoxSelect className="h-5 w-5 text-orange-500" />
              {t('gallery.addProductTitle')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-2 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:text-neutral-300 dark:hover:bg-neutral-800">
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Error Alert */}
            {error && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
                <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Bild-Vorschau */}
            {image && (
              <div className="flex gap-4 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                <img
                  src={`/thumb/${image.id}`}
                  alt={image.prompt || 'Bild'}
                  className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-neutral-700 dark:text-neutral-200 line-clamp-2 mb-2">
                    {image.prompt || '(Kein Prompt)'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{image.ratio}</Badge>
                    <Badge variant="default">{image.template_name || image.preset_type}</Badge>
                  </div>
                </div>
              </div>
            )}

            {/* Produkt-Suche */}
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input
                placeholder="Produkt suchen (SKU, Name, GTIN)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Produkt-Tabelle */}
            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
              {/* Tabellen-Header */}
              <div className="grid grid-cols-[auto_1fr_auto] gap-3 px-3 py-2 bg-neutral-100 dark:bg-neutral-800 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                <div className="w-20">SKU</div>
                <div>Name</div>
                <div className="w-8"></div>
              </div>

              {/* Tabellen-Body */}
              <div className="max-h-[240px] overflow-y-auto">
                {isLoadingProducts ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                  </div>
                ) : products.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    {searchQuery ? t('common.noResults') : t('common.search')}
                  </div>
                ) : (
                  products.map((product: Product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => setSelectedProductId(product.id)}
                      className={cn(
                        'w-full grid grid-cols-[auto_1fr_auto] gap-3 px-3 py-3 text-left text-sm',
                        'border-b border-neutral-100 dark:border-neutral-800 last:border-0',
                        'hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors',
                        selectedProductId === product.id && 'bg-orange-50 dark:bg-orange-900/20'
                      )}
                    >
                      <div className="w-20 font-mono text-neutral-600 dark:text-neutral-300 truncate">
                        {product.sku || '-'}
                      </div>
                      <div className="text-neutral-900 dark:text-neutral-100 truncate">
                        {product.name}
                      </div>
                      <div className="w-8 flex items-center justify-center">
                        {selectedProductId === product.id ? (
                          <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-neutral-300 dark:border-neutral-600" />
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Ziel-Auswahl */}
            <div className={targetType === 'ai_reference' ? 'grid grid-cols-2 gap-3' : ''}>
              <Select
                label={t('gallery.addTo')}
                options={[
                  { value: 'collection', label: 'Bildersammlung' },
                  { value: 'ai_reference', label: 'KI-Referenzbild' },
                ]}
                value={targetType}
                onValueChange={(v) => {
                  setTargetType(v as 'ai_reference' | 'collection');
                  setSelectedSlot(1);
                }}
              />
              {targetType === 'ai_reference' && (
                <Select
                  label="Slot (1-4)"
                  options={[1, 2, 3, 4].map((n) => ({ value: String(n), label: `Slot ${n}` }))}
                  value={String(selectedSlot)}
                  onValueChange={(v) => setSelectedSlot(Number(v))}
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 dark:border-neutral-700">
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleTransfer}
              disabled={!selectedProductId || transferMutation.isPending}
            >
              {transferMutation.isPending ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {t('gallery.transferring')}
                </>
              ) : (
                <>
                  <BoxSelect className="h-4 w-4 mr-2" />
                  {t('gallery.addButton')}
                </>
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default TransferToProductDialog;
